/**
 * Definição declarativa de todos os steps do onboarding cinemático Dr. Pera IA.
 * Cada step mapeia um elemento do DOM para um título + descrição i18n.
 *
 * Steps com selectorTarget=undefined são fullscreen (welcome/final).
 * Steps com action='open-profile-modal' pausam o tour e abrem o ProfileModal real.
 */

export type OnboardingStepPosition = 'top' | 'bottom' | 'left' | 'right' | 'center';
export type OnboardingStepAction = 'open-profile-modal' | null;

export interface OnboardingStep {
  /** ID único do step */
  id: string;
  /** CSS selector do elemento alvo (undefined = fullscreen sem spotlight) */
  selectorTarget?: string;
  /** i18n key do título do step */
  titleKey: string;
  /** i18n key da descrição do step */
  descKey: string;
  /** i18n key do texto do botão CTA (padrão: 'onboarding.next') */
  ctaKey?: string;
  /** Ação especial do step (abre modal real, etc.) */
  action?: OnboardingStepAction;
  /** Posição do tooltip em relação ao elemento alvo */
  position?: OnboardingStepPosition;
}

/**
 * Lista completa dos steps do onboarding.
 * Ordem: welcome → perfil (modal) → features → final
 */
export const ONBOARDING_STEPS: OnboardingStep[] = [
  // Step 0 — Welcome fullscreen com BootSequence cinemática
  {
    id: 'welcome',
    titleKey: 'onboarding.welcome.title',
    descKey: 'onboarding.welcome.subtitle',
    ctaKey: 'onboarding.welcome.cta',
    position: 'center',
  },

  // Step 1 — Perfil IA (step interativo — abre ProfileModal real)
  {
    id: 'profile',
    selectorTarget: '[data-onboarding="header-profile"]',
    titleKey: 'onboarding.steps.profile.title',
    descKey: 'onboarding.steps.profile.desc',
    ctaKey: 'onboarding.steps.profile.cta',
    action: 'open-profile-modal',
    position: 'bottom',
  },

  // 'templates' removido 20/05/2026 — botão TEMPLATES oculto no Header.
  // Mantida prop onOpenTemplates pra reincorporar no futuro.

  // Step 2 — Modo SOAP/prontuário
  {
    id: 'soap',
    selectorTarget: '[data-onboarding="header-soap"]',
    titleKey: 'onboarding.steps.soap.title',
    descKey: 'onboarding.steps.soap.desc',
    position: 'bottom',
  },

  // Step 4 — Plantão 12h
  {
    id: 'plantao',
    selectorTarget: '[data-onboarding="header-plantao"]',
    titleKey: 'onboarding.steps.plantao.title',
    descKey: 'onboarding.steps.plantao.desc',
    position: 'bottom',
  },

  // Step 5 — Voice (ditado por celular via QR pairing) — só Esp/Unlimited usam
  {
    id: 'voice',
    selectorTarget: '[data-onboarding="header-voice"]',
    titleKey: 'onboarding.steps.voice.title',
    descKey: 'onboarding.steps.voice.desc',
    position: 'bottom',
  },

  // Step 6 — Badge de créditos
  {
    id: 'credits',
    selectorTarget: '[data-onboarding="credits-badge"]',
    titleKey: 'onboarding.steps.credits.title',
    descKey: 'onboarding.steps.credits.desc',
    position: 'bottom',
  },

  // Step 6 — Clip de anexo (imagens, RX, ECG, labs)
  {
    id: 'attach',
    selectorTarget: '[data-onboarding="attach-clip"]',
    titleKey: 'onboarding.steps.attach.title',
    descKey: 'onboarding.steps.attach.desc',
    position: 'top',
  },

  // Step 7 — Textarea principal (onde o médico digita)
  {
    id: 'textarea',
    selectorTarget: 'textarea',
    titleKey: 'onboarding.steps.textarea.title',
    descKey: 'onboarding.steps.textarea.desc',
    position: 'top',
  },

  // Step 8 — Final fullscreen com CTA
  {
    id: 'final',
    titleKey: 'onboarding.final.title',
    descKey: 'onboarding.final.subtitle',
    ctaKey: 'onboarding.final.cta',
    position: 'center',
  },
];

/** Total de steps "intermediários" (exclui welcome e final) — para barra de progresso */
export const STEPS_INTERMEDIARIOS = ONBOARDING_STEPS.filter(
  (s) => s.id !== 'welcome' && s.id !== 'final',
).length;
